import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	TextField,
	InputAdornment,
	IconButton,
	Grid,
	Paper,
	Typography,
	Checkbox,
	FormControlLabel,
	Avatar,
	ListItemText
  } from '@material-ui/core';

import { Visibility, VisibilityOff, PhotoCamera } from '@material-ui/icons';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../helpers/cropImage';

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	avatarContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		marginBottom: theme.spacing(2),
	},
	avatar: {
		width: theme.spacing(12),
		height: theme.spacing(12),
		marginBottom: theme.spacing(1),
	},
	uploadInput: {
		display: 'none',
	},
	permissionsContainer: {
		marginTop: theme.spacing(2),
		padding: theme.spacing(2),
		backgroundColor: theme.palette.background.default,
		borderRadius: theme.shape.borderRadius,
	},
	permissionTitle: {
		marginBottom: theme.spacing(1),
		fontWeight: 'bold',
	},
	cropContainer: {
		position: 'relative',
		width: '100%',
		height: 300,
		background: '#333',
		[theme.breakpoints.up('sm')]: {
			height: 400,
		},
	},
	cropControls: {
		padding: theme.spacing(2),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	cropButton: {
		marginTop: theme.spacing(2),
	}
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "¡Muy corto!")
		.max(50, "¡Muy largo!")
		.required("Requerido"),
	password: Yup.string().min(5, "¡Muy corto!").max(50, "¡Muy largo!"),
	email: Yup.string().email("Email inválido").required("Requerido"),
});

const UserModal = ({ open, onClose, userId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user"
	};

	const modules = [
		{ id: "dashboard", name: "Dashboard" },
		{ id: "agente", name: "Agente" },
		{ id: "connections", name: "Conexiones" },
		{ id: "masivo", name: "Masivo" },
		{ id: "tickets", name: "Chat" },
		{ id: "contacts", name: "Contactos" },
		{ id: "settings", name: "Ajustes (General)" }
	];

	const { user: loggedInUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [showPassword, setShowPassword] = useState(false);
	const [whatsappIds, setWhatsappIds] = useState([]);
	const [allTicketConnections, setAllTicketConnections] = useState("forbidden");
	const [assignedFolders, setAssignedFolders] = useState([]);
	const [folders, setFolders] = useState([]);
	const [profileImage, setProfileImage] = useState("");
	const [permissions, setPermissions] = useState(modules.reduce((acc, mod) => ({...acc, [mod.id]: true}), {}));
	const {loading, whatsApps} = useWhatsApps();
	const [systemLabels, setSystemLabels] = useState([]);

	// Cropper state
	const [imageSrc, setImageSrc] = useState(null);
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
	const [isCropModalOpen, setIsCropModalOpen] = useState(false);

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);
				setUser(prevState => {
					return { ...prevState, ...data };
				});
				const userQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(userQueueIds);
				
				if (data.whatsapps && data.whatsapps.length > 0) {
					setWhatsappIds(data.whatsapps.map(w => w.id));
				} else {
					setWhatsappIds(data.whatsappId ? [data.whatsappId] : []);
				}
				
				setAllTicketConnections(data.allTicketConnections ? data.allTicketConnections : 'forbidden');
				
				if (data.assignedFolders) {
					try {
						const parsedFolders = typeof data.assignedFolders === 'string' ? JSON.parse(data.assignedFolders) : data.assignedFolders;
						setAssignedFolders(Array.isArray(parsedFolders) ? parsedFolders : []);
					} catch (e) {
						console.error("Error parsing assignedFolders", e);
						setAssignedFolders([]);
					}
				} else {
					setAssignedFolders([]);
				}

				setProfileImage(data.profileImage || "");
				if (data.permissions) {
					// Si los permisos vienen como string JSON, los parseamos
					const parsedPermissions = typeof data.permissions === 'string' 
						? JSON.parse(data.permissions) 
						: data.permissions;
					setPermissions(parsedPermissions);
				} else {
					setPermissions(modules.reduce((acc, mod) => ({...acc, [mod.id]: true}), {}));
				}
			} catch (err) {
				toastError(err);
			}
		};

		const fetchFoldersAndLabels = async () => {
			try {
				const [settingsRes, labelsRes] = await Promise.all([
					api.get("/settings"),
					api.get("/labels")
				]);
				
				const registry = settingsRes.data.find(s => s.key === "tagFolders");
				if (registry && registry.value) {
					const parsedFolders = JSON.parse(registry.value);
					setFolders(parsedFolders);
				}

				// Guardar las etiquetas de sistema (isSystem = true)
				if (labelsRes.data) {
					setSystemLabels(labelsRes.data.filter(l => l.isSystem));
				}
			} catch (e) {
				console.error("Error cargando carpetas o etiquetas", e);
			}
		};

		fetchFoldersAndLabels();
		fetchUser();
	}, [userId, open]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
		setProfileImage("");
		setWhatsappIds([]);
		setAssignedFolders([]);
		setAllTicketConnections("forbidden");
		setPermissions(modules.reduce((acc, mod) => ({...acc, [mod.id]: true}), {}));
	};

	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			const reader = new FileReader();
			reader.onloadend = () => {
				setImageSrc(reader.result);
				setIsCropModalOpen(true);
			};
			reader.readAsDataURL(file);
		}
	};

	const onCropComplete = (croppedArea, croppedAreaPixels) => {
		setCroppedAreaPixels(croppedAreaPixels);
	};

	const handleCropSave = async () => {
		try {
			const croppedImageBase64 = await getCroppedImg(imageSrc, croppedAreaPixels);
			setProfileImage(croppedImageBase64);
			setIsCropModalOpen(false);
			setImageSrc(null); // Clear memory
		} catch (e) {
			console.error(e);
			toast.error("Error al recortar la imagen");
		}
	};

	const handlePermissionChange = (e) => {
		setPermissions({ ...permissions, [e.target.name]: e.target.checked });
	};

	const handleSelectAll = () => {
		const allChecked = modules.reduce((acc, mod) => ({ ...acc, [mod.id]: true }), {});
		setPermissions(allChecked);
	};

	const handleDeselectAll = () => {
		const allUnchecked = modules.reduce((acc, mod) => ({ ...acc, [mod.id]: false }), {});
		setPermissions(allUnchecked);
	};

	const handleSaveUser = async values => {
		const userData = { 
			...values, 
			whatsappId: allTicketConnections === "allowed" ? null : (whatsappIds.length > 0 ? whatsappIds[0] : null), 
			whatsappIds: allTicketConnections === "allowed" ? [] : whatsappIds,
			allTicketConnections,
			assignedFolders: assignedFolders, // Aseguramos que se envíe el array tal cual
			queueIds: selectedQueueIds, 
			profileImage, 
			permissions 
		};
		try {
			if (userId) {
				await api.put(`/users/${userId}`, userData);
			} else {
				await api.post("/users", userData);
			}
			toast.success(i18n.t("userModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.avatarContainer}>
									<input
										accept="image/*"
										className={classes.uploadInput}
										id="icon-button-file"
										type="file"
										onChange={handleImageChange}
									/>
									<label htmlFor="icon-button-file">
										<IconButton color="primary" aria-label="upload picture" component="span">
											<Avatar 
												src={profileImage} 
												className={classes.avatar} 
												style={{ cursor: 'pointer' }}
											>
												{!profileImage && <PhotoCamera fontSize="large" />}
											</Avatar>
										</IconButton>
									</label>
									<Typography variant="caption" color="textSecondary">
										Clic para cambiar foto
									</Typography>
								</div>

								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<Field
										as={TextField}
										name="password"
										variant="outlined"
										margin="dense"
										label={i18n.t("userModal.form.password")}
										error={touched.password && Boolean(errors.password)}
										helperText={touched.password && errors.password}
										type={showPassword ? 'text' : 'password'}
										InputProps={{
										endAdornment: (
											<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={() => setShowPassword((e) => !e)}
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
											</InputAdornment>
										)
										}}
										fullWidth
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<FormControl
										variant="outlined"
										className={classes.formControl}
										margin="dense"
									>
										<Can
											role={loggedInUser.profile}
											perform="user-modal:editProfile"
											yes={() => (
												<>
													<InputLabel id="profile-selection-input-label">
														{i18n.t("userModal.form.profile")}
													</InputLabel>

													<Field
														as={Select}
														label={i18n.t("userModal.form.profile")}
														name="profile"
														labelId="profile-selection-label"
														id="profile-selection"
														required
													>
														<MenuItem value="admin">Admin</MenuItem>
														<MenuItem value="user">User</MenuItem>
													</Field>
												</>
											)}
										/>
									</FormControl>
								</div>
								{/* Ocultado temporalmente por solicitud del usuario
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (
										<QueueSelect
											selectedQueueIds={selectedQueueIds}
											onChange={values => setSelectedQueueIds(values)}
										/>
									)}
								/>
								*/}
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (!loading && (
										values.profile === 'admin' ? (
											<div style={{ padding: '16px 0', textAlign: 'center' }}>
												<Typography variant="body2" color="textSecondary">
													Como Administrador, este usuario tiene <strong>acceso total</strong> a todas las conexiones, carpetas y etiquetas del sistema.
												</Typography>
											</div>
										) : (
											<>
												<FormControl variant="outlined" margin="dense" className={classes.maxWidth} fullWidth>
													<InputLabel>{i18n.t("userModal.form.whatsapp")}</InputLabel>
													<Select
														multiple
														value={allTicketConnections === "allowed" ? ["all"] : whatsappIds}
														onChange={(e) => {
															const selectedValues = e.target.value;
															if (selectedValues.includes("all")) {
																setAllTicketConnections("allowed");
																setWhatsappIds([]);
															} else {
																setAllTicketConnections("forbidden");
																setWhatsappIds(selectedValues);
																
																// Limpiar carpetas asignadas que ya no correspondan a las nuevas conexiones (respetando la de sistema)
																const validFolders = folders.filter(f => 
																	f.id === "system_folder" || selectedValues.some(wId => String(wId) === String(f.whatsappId))
																).map(f => f.id);
																setAssignedFolders(prev => prev.filter(id => validFolders.includes(id)));
															}
														}}
														label={i18n.t("userModal.form.whatsapp")}
														renderValue={(selected) => {
															if (allTicketConnections === "allowed") return "Todas las conexiones";
															if (selected.length === 0) return "Seleccione conexiones";
															return selected.map(id => whatsApps.find(w => w.id === id)?.name).join(", ");
														}}
													>
														<MenuItem value={'all'}>
															<Checkbox checked={allTicketConnections === "allowed"} />
															<ListItemText primary="Todas las conexiones" />
														</MenuItem>
														{whatsApps.map((whatsapp) => (
															<MenuItem key={whatsapp.id} value={whatsapp.id}>
																<Checkbox checked={whatsappIds.indexOf(whatsapp.id) > -1} />
																<ListItemText primary={whatsapp.name} />
															</MenuItem>
														))}
													</Select>
												</FormControl>

												{folders.length > 0 && (
													<FormControl variant="outlined" margin="dense" className={classes.maxWidth} fullWidth>
														<InputLabel>Carpetas de Conexión Asignadas</InputLabel>
														<Select
															multiple
															value={Array.isArray(assignedFolders) ? assignedFolders.filter(id => {
																const f = folders.find(folder => folder.id === id);
																return f && f.name !== 'Sistema';
															}) : []}
															onChange={(e) => {
																// Al cambiar, mantenemos las etiquetas de sistema (que se guardan como strings especiales o IDs de etiquetas)
																const systemIds = Array.isArray(assignedFolders) ? assignedFolders.filter(id => typeof id === 'string' && id.startsWith('sys_label_')) : [];
																setAssignedFolders([...systemIds, ...e.target.value]);
															}}
															label="Carpetas de Conexión Asignadas"
															renderValue={(selected) => {
																if (selected.length === 0) return "Ninguna carpeta asignada";
																return selected.map(id => {
																	const f = folders.find(folder => folder.id === id);
																	return f ? f.name : "";
																}).filter(Boolean).join(", ");
															}}
														>
															{folders.filter(f => {
																if (f.name === 'Sistema') return false;
																// Si el usuario tiene acceso a "Todas" las conexiones, mostramos todas las carpetas
																if (allTicketConnections === "allowed") return true;
																// Si tiene conexiones seleccionadas, filtramos las carpetas por la conexión
																if (whatsappIds && whatsappIds.length > 0) {
																	return whatsappIds.some(wId => String(wId) === String(f.whatsappId));
																}
																// Si no tiene conexión seleccionada, no mostramos ninguna de conexión
																return false;
															}).map((folder) => (
																<MenuItem key={folder.id} value={folder.id}>
																	<Checkbox checked={Array.isArray(assignedFolders) && assignedFolders.indexOf(folder.id) > -1} />
																	<ListItemText primary={folder.name} />
																</MenuItem>
															))}
														</Select>
													</FormControl>
												)}

												{systemLabels.length > 0 && (
													<FormControl variant="outlined" margin="dense" className={classes.maxWidth} fullWidth>
														<InputLabel>Etiquetas de Sistema (Globales)</InputLabel>
														<Select
															multiple
															value={Array.isArray(assignedFolders) ? assignedFolders.filter(id => typeof id === 'string' && id.startsWith('sys_label_')) : []}
															onChange={(e) => {
																// Al cambiar, mantenemos las carpetas de conexión y agregamos las nuevas etiquetas de sistema seleccionadas
																const connectionIds = Array.isArray(assignedFolders) ? assignedFolders.filter(id => typeof id === 'number' || (typeof id === 'string' && !id.startsWith('sys_label_'))) : [];
																setAssignedFolders([...connectionIds, ...e.target.value]);
															}}
															label="Etiquetas de Sistema (Globales)"
															renderValue={(selected) => {
																if (selected.length === 0) return "Ninguna etiqueta de sistema asignada";
																return selected.map(id => {
																	const rawId = id.replace('sys_label_', '');
																	const label = systemLabels.find(l => String(l.id) === String(rawId));
																	return label ? label.name : "";
																}).filter(Boolean).join(", ");
															}}
														>
															{systemLabels.map((label) => {
																const compoundId = `sys_label_${label.id}`;
																return (
																	<MenuItem key={compoundId} value={compoundId}>
																		<Checkbox checked={Array.isArray(assignedFolders) && assignedFolders.indexOf(compoundId) > -1} />
																		<ListItemText primary={label.name} />
																	</MenuItem>
																);
															})}
														</Select>
													</FormControl>
												)}
											</>
										)
									))}
								/>

								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (
										<Paper className={classes.permissionsContainer} variant="outlined">
											<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
												<Typography variant="subtitle1" className={classes.permissionTitle} style={{ margin: 0 }}>
													Permisos de Módulos (Menú)
												</Typography>
												<div>
													<Button size="small" color="primary" onClick={handleSelectAll}>
														Todos
													</Button>
													<Button size="small" color="secondary" onClick={handleDeselectAll}>
														Ninguno
													</Button>
												</div>
											</div>
											<Grid container spacing={2}>
												{modules.map((module) => (
													<Grid item xs={6} sm={4} key={module.id}>
														<FormControlLabel
															control={
																<Checkbox
																	checked={permissions[module.id] || false}
																	onChange={handlePermissionChange}
																	name={module.id}
																	color="primary"
																/>
															}
															label={module.name}
														/>
													</Grid>
												))}
											</Grid>
										</Paper>
									)}
								/>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{userId
										? `${i18n.t("userModal.buttons.okEdit")}`
										: `${i18n.t("userModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>

			{/* Crop Modal */}
			<Dialog
				open={isCropModalOpen}
				onClose={() => setIsCropModalOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Recortar Imagen</DialogTitle>
				<DialogContent dividers>
					<div className={classes.cropContainer}>
						<Cropper
							image={imageSrc}
							crop={crop}
							zoom={zoom}
							aspect={1}
							cropShape="round"
							showGrid={false}
							onCropChange={setCrop}
							onCropComplete={onCropComplete}
							onZoomChange={setZoom}
						/>
					</div>
					<div className={classes.cropControls}>
						<Typography variant="overline">Zoom</Typography>
						<input
							type="range"
							value={zoom}
							min={1}
							max={3}
							step={0.1}
							aria-labelledby="Zoom"
							onChange={(e) => {
								setZoom(e.target.value)
							}}
							style={{ width: '100%' }}
						/>
					</div>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsCropModalOpen(false)} color="secondary">
						Cancelar
					</Button>
					<Button onClick={handleCropSave} color="primary" variant="contained">
						Aplicar
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default UserModal;
